<template>
  <div class="social-networks-row">
    <template v-if="props.post?.postToAccounts?.length">
      <ul class="social-networks-row__list">
        <li class="social-networks-row__logo-wrap" :style="logoWrapStyle" v-for="socialLogo in orderedPostToAccounts" :key="socialLogo">
          <img
            :class="['social-networks-row__logo', { 'social-networks-row__logo--inactive': props.isUnusedPost }]"
            :src="`/images/social-logo-${socialLogo.toLowerCase()}.svg`"
            :alt="getSocialChannelLabel(socialLogo)"
          />
        </li>
        <LinkWithIcon v-if="props.isEditable && !props.isUnusedPost" color="gray" isDottedUnderline style="flex-shrink: 0" @click="isPickSocialsModalVisible = true">
          <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
        </LinkWithIcon>
      </ul>
    </template>

    <template v-else>
      <ul class="social-networks-row__list">
        <li class="social-networks-row__logo-wrap" :style="logoWrapStyle">
          <img class="social-networks-row__logo social-networks-row__logo--inactive" src="/images/social-logo-instagram.svg" alt="Instagram" />
        </li>
        <li class="social-networks-row__logo-wrap" :style="logoWrapStyle">
          <img class="social-networks-row__logo social-networks-row__logo--inactive" src="/images/social-logo-facebook.svg" alt="Facebook" />
        </li>
        <TooltipWrapper v-if="props.isEditable && !props.isUnusedPost">
          <template #hoverableElement>
            <LinkWithIcon isDottedUnderline @click="isPickSocialsModalVisible = true" color="light-gray" tooltip="Connect your Accounts">
              <template #icon><IconEmbedded name="plus_2-5" :size="18" /></template>
            </LinkWithIcon>
          </template>
          <template #tooltip>Connect your Accounts</template>
        </TooltipWrapper>
      </ul>
    </template>

    <PickSocialNetworksModal
      v-if="isPickSocialsModalVisible && props.isEditable && props.post"
      v-model:isVisible="isPickSocialsModalVisible"
      :allCustomerSocialChannels="props.allCustomerSocialChannels"
      :selectedSocialChannels="props.post.postToAccounts"
      :post="props.post"
      @save="selectedSocialChannels => emit('updatePostToAccounts', selectedSocialChannels)"
      @addSocialAccount="emit('addSocialAccount')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import PickSocialNetworksModal from "@components/SocialPostPreview/PickSocialNetworksModal.vue";
import { SocialMediaPostData } from "@contracts/shareStop";
import { getSocialChannelLabel, SocialChannels, sortSocialChannelsForDisplay } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

const props = withDefaults(
  defineProps<{
    post: SocialMediaPostData | null;
    allCustomerSocialChannels?: Array<SocialChannelData>;
    isUnusedPost?: boolean;
    isEditable?: boolean;
    logoSizePx?: number;
  }>(),
  {
    post: null,
    allCustomerSocialChannels: () => [],
    isUnusedPost: false,
    isEditable: false,
    logoSizePx: 21,
  }
);

const emit = defineEmits<{
  (e: "updatePostToAccounts", selectedSocialChannels: Array<SocialChannels>): void;
  (e: "addSocialAccount"): void;
}>();

const isPickSocialsModalVisible = ref<boolean>(false);
const logoWrapStyle = computed(() => ({ width: `${props.logoSizePx}px` }));
const orderedPostToAccounts = computed<Array<SocialChannels>>(() => sortSocialChannelsForDisplay(props.post?.postToAccounts ?? []));
</script>

<style scoped lang="scss">
.social-networks-row {
  flex-shrink: 0;

  &__list {
    gap: 8px;
    padding: 0;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    list-style: none;
    margin-bottom: 0;
    margin-top: 0;
  }

  &__logo-wrap {
    width: 21px;
    display: flex;
    align-items: center;
  }

  &__logo {
    width: 100%;

    &--inactive {
      opacity: 0.4;
      filter: grayscale(1);
    }
  }
}
</style>
